import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError, BACKEND_URL, tokenStore } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { t } from "../lib/i18n";
import Logo from "../components/Logo";
import PhotoUploader from "../components/PhotoUploader";
import { LogOut, Download, Trash2, ShieldCheck, Sparkles, FileText, ExternalLink,
         Clock, Camera, Video, MessageSquare, Star, MapPin, LifeBuoy, ShoppingBag,
         Award, ClipboardCheck, ClipboardList, ShieldAlert, AlertTriangle, Bell,
         Receipt, Menu as MenuIcon, X, ChevronRight,
} from "lucide-react";
import WelcomeBanner from "../components/WelcomeBanner";
import CustomerMessages from "../components/CustomerMessages";
import CustomerSurvey from "../components/CustomerSurvey";
import ShareLocationCard from "../components/ShareLocationCard";
import CustomerExtras from "../components/CustomerExtras";
import CustomerAccount from "../components/CustomerAccount";
import PushSettings from "../components/PushSettings";
import SelfCheckWizard from "../components/SelfCheckWizard";
import CustomerTimeChangeRequest from "../components/CustomerTimeChangeRequest";
import CustomerIncidentsCard from "../components/CustomerIncidentsCard";
import CustomerLifeVests from "../components/CustomerLifeVests";
import CustomerDepartureChecklist from "../components/CustomerDepartureChecklist";
import CustomerPhotoShare from "../components/CustomerPhotoShare";
import CustomerVideoShare from "../components/CustomerVideoShare";
import CustomerCaptainCard from "../components/CustomerCaptainCard";
import CustomerBoatInspection from "../components/CustomerBoatInspection";
import CustomerReceiptPreview from "../components/CustomerReceiptPreview";

export default function CustomerPortal() {
  const { user, logout, refresh, setUser } = useAuth();
  const navigate = useNavigate();
  // English is the customer default. Customers can switch to Norwegian via
  // the language picker; their choice is persisted on the user record.
  const lang = user?.language === "no" ? "no" : "en";
  const [booking, setBooking] = useState(null);
  const [guests, setGuests] = useState(0);
  const [reports, setReports] = useState([]);
  const [report, setReport] = useState({ title: "", description: "", photo_ids: [] });
  const [savingGuests, setSavingGuests] = useState(false);
  const [sendingReport, setSendingReport] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [delOpen, setDelOpen] = useState(false);
  const [delPw, setDelPw] = useState("");
  const [delConfirm, setDelConfirm] = useState("");
  const [docs, setDocs] = useState({ global: [], for_booking: [] });
  const [showWelcome, setShowWelcome] = useState(false);
  const [accountRefreshKey, setAccountRefreshKey] = useState(0);
  const [selfCheck, setSelfCheck] = useState({
    self_checkin_enabled: false, self_checkout_enabled: false, submissions: [],
  });
  // Section chosen from the "Meny"-grid. When set, only that section is
  // rendered inside a full-width dialog so the customer doesn't have to
  // scroll through everything.
  const [activeSection, setActiveSection] = useState(null);
  // Whether the mandatory insurance for this booking has been paid. Once
  // paid, checklists are promoted to the main portal so the customer can
  // see them without opening a menu.
  const [insurancePaid, setInsurancePaid] = useState(false);

  const loadInsuranceStatus = async (bookingId) => {
    if (!bookingId) { setInsurancePaid(false); return; }
    try {
      const r = await api.get("/orders", { params: { booking_id: bookingId } });
      const paid = (r.data || []).some(
        (o) =>
          o.status === "paid"
          && (o.items || []).some((i) => i.category === "insurance"),
      );
      setInsurancePaid(paid);
    } catch {
      setInsurancePaid(false);
    }
  };

  const loadSelfChecks = async (bookingId) => {
    if (!bookingId) {
      setSelfCheck({ self_checkin_enabled: false, self_checkout_enabled: false, submissions: [] });
      return;
    }
    try {
      const r = await api.get(`/self-checks/${bookingId}`);
      setSelfCheck(r.data);
    } catch {
      setSelfCheck({ self_checkin_enabled: false, self_checkout_enabled: false, submissions: [] });
    }
  };

  const load = async () => {
    try {
      const { data } = await api.get("/my-booking");
      setBooking(data);
      setGuests(data?.num_guests || 0);
      setReports([]);
      await loadSelfChecks(data?.id);
      await loadInsuranceStatus(data?.id);
    } catch {
      setBooking(null);
      setSelfCheck({ self_checkin_enabled: false, self_checkout_enabled: false, submissions: [] });
    }
    try {
      const r = await api.get("/documents/customer");
      setDocs(r.data || { global: [], for_booking: [] });
    } catch {
      setDocs({ global: [], for_booking: [] });
    }
  };

  // Show the big welcome card on the very first portal visit. We don't want
  // it nagging on every reload, so we hide it locally for the session AND
  // tell the backend to record welcome_seen_at on dismiss.
  useEffect(() => {
    if (!user) return;
    const dismissed = sessionStorage.getItem("welcome_dismissed");
    if (dismissed) return;
    if (!user.welcome_seen_at) {
      setShowWelcome(true);
    }
  }, [user]);

  const dismissWelcome = async () => {
    setShowWelcome(false);
    sessionStorage.setItem("welcome_dismissed", "1");
    try {
      await api.post("/auth/welcome-seen");
    } catch { /* non-blocking */ }
  };

  const docUrl = (fileId) => {
    const token = tokenStore.get();
    return `${BACKEND_URL}/api/files/${fileId}?auth=${encodeURIComponent(token)}`;
  };

  useEffect(() => { load(); }, []);

  const setLang = async (newLang) => {
    await api.patch("/auth/language", { language: newLang });
    await refresh();
  };

  const saveGuests = async () => {
    if (!booking) return;
    setSavingGuests(true);
    try {
      await api.patch(`/bookings/${booking.id}`, { num_guests: parseInt(guests, 10) });
      toast.success(t(lang, "guests_saved"));
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSavingGuests(false);
    }
  };

  const sendReport = async () => {
    if (!booking) return;
    if (!report.title.trim() || !report.description.trim()) {
      return toast.error(lang === "en" ? "Please fill in subject and description." : "Fyll inn tittel og beskrivelse.");
    }
    setSendingReport(true);
    try {
      await api.post("/customer-reports", { ...report, booking_id: booking.id });
      toast.success(t(lang, "report_sent"));
      setReport({ title: "", description: "", photo_ids: [] });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSendingReport(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <header className="bg-[#0C1824] text-white">
        <div className="max-w-5xl mx-auto px-3 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-2">
          <Logo dark size={32} />
          <div className="flex items-center gap-2 sm:gap-3">
            <Select value={lang} onValueChange={setLang}>
              <SelectTrigger className="h-9 w-[92px] sm:w-[110px] bg-white/10 border-white/20 text-white" data-testid="lang-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="no">Norsk</SelectItem>
                <SelectItem value="en">English</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              onClick={() => logout()}
              data-testid="customer-logout"
              className="border-white/20 text-white hover:bg-white/10 px-2.5 sm:px-4"
            >
              <LogOut className="w-4 h-4 sm:mr-1" />
              <span className="hidden sm:inline">{t(lang, "logout")}</span>
            </Button>
          </div>
        </div>
      </header>

      <section className="relative">
        <img
          src={booking?.boat ? (booking.boat.length_ft === 33 ? "/boat-33-side.jpg" : "/boat-39-side.jpg") : "/boat-39-side.jpg"}
          alt=""
          className="w-full h-56 sm:h-72 object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0C1824]/80 via-[#0C1824]/40 to-transparent" />
        <div className="absolute inset-0 flex items-center">
          <div className="max-w-5xl w-full mx-auto px-4 sm:px-6">
            <div className="text-xs uppercase tracking-[0.32em] text-[#ECC974]">
              {t(lang, "eco_slogan")}
            </div>
            <h1 className="text-3xl sm:text-5xl font-bold text-white mt-2">
              {t(lang, "welcome")}, {user?.name?.split(" ")[0]}
            </h1>
          </div>
        </div>
      </section>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-10 space-y-6 sm:space-y-8">
        {showWelcome && (
          <Card
            data-testid="welcome-aboard-card"
            className="relative overflow-hidden p-7 sm:p-9 bg-gradient-to-br from-[#11283A] via-[#0C1824] to-[#1DA3B3]/40 text-white border-0"
          >
            <div
              aria-hidden
              className="absolute -right-12 -top-12 w-64 h-64 rounded-full bg-[#BD984D]/15 blur-3xl pointer-events-none"
            />
            <div className="absolute right-6 top-6 z-10">
              <button
                onClick={dismissWelcome}
                data-testid="welcome-aboard-dismiss"
                aria-label={t(lang, "welcome_dismiss")}
                className="text-white/70 hover:text-white text-xs uppercase tracking-[0.22em] border border-white/20 rounded-full px-3 py-1 hover:bg-white/10"
              >
                {t(lang, "welcome_dismiss")}
              </button>
            </div>
            <div className="relative">
              <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.32em] text-[#ECC974]">
                <Sparkles className="w-3.5 h-3.5" /> {t(lang, "eco_slogan")}
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold mt-2">
                {t(lang, "welcome_aboard")}
              </h2>
              <p className="text-sm sm:text-base text-white/85 mt-3 max-w-2xl leading-relaxed">
                {t(lang, "welcome_aboard_body")}
              </p>
              {booking?.boat?.name && (
                <div className="mt-5 text-sm text-white/70">
                  <span className="text-[#ECC974] font-mono">{booking.boat.name}</span>
                  {booking.start_date && (
                    <> · {booking.start_date.slice(0, 10)} → {booking.end_date.slice(0, 10)}</>
                  )}
                </div>
              )}
            </div>
          </Card>
        )}

        <WelcomeBanner />
        {!booking ? (
          <Card className="p-8 text-center text-[#2B3F4D]" data-testid="no-booking">
            {t(lang, "no_booking")}
          </Card>
        ) : (
          <>
            <Card className="overflow-hidden">
              <div className="grid md:grid-cols-2">
                <div className="relative h-56 md:h-auto bg-slate-100">
                  <img
                    src={booking?.boat?.length_ft === 33 ? "/boat-33-rear.jpg" : "/boat-39-rear.jpg"}
                    alt={booking?.boat?.name}
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                </div>
                <div className="p-6 sm:p-8">
                  <div className="text-xs uppercase tracking-[0.32em] text-[#A57F39]">
                    {t(lang, "your_boat")}
                  </div>
                  <div className="text-2xl sm:text-3xl font-bold text-[#0C1824] mt-1">
                    {booking.boat?.name || t(lang, "no_boat")}
                  </div>
                  <div className="text-sm text-[#2B3F4D] mt-1">
                    {booking.boat?.model} · {booking.boat?.length_ft}&prime;
                  </div>

                  <div className="mt-5 pt-5 border-t border-[#E2E8F0]">
                    <div className="text-xs uppercase tracking-[0.18em] text-[#A57F39]">
                      {t(lang, "booking_period")}
                    </div>
                    <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div
                        className="p-3 rounded-md border-l-4 border-l-[#1DA3B3] bg-[#1DA3B3]/5"
                        data-testid="customer-checkin"
                      >
                        <div className="text-[10px] uppercase tracking-[0.18em] text-[#1DA3B3] font-semibold">
                          {t(lang, "checkin_label")}
                        </div>
                        <div className="text-base font-semibold text-[#0C1824] mt-1">
                          {booking.start_date?.slice(0, 10)}
                        </div>
                        <div className="text-sm font-medium text-[#1DA3B3] mt-0.5">
                          {booking.checkin_time
                            ? (lang === "en"
                                ? `from ${booking.checkin_time}`
                                : `fra kl. ${booking.checkin_time}`)
                            : t(lang, "checkin_time")}
                        </div>
                        {booking.checkin_time && booking.checkin_time < "14:00" && (
                          <div
                            className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-widest text-emerald-700 bg-emerald-100 rounded px-2 py-0.5 mt-1.5"
                            data-testid="customer-checkin-early-badge"
                          >
                            {lang === "en" ? "Early check-in" : "Tidlig innsjekk"}
                          </div>
                        )}
                      </div>
                      <div
                        className="p-3 rounded-md border-l-4 border-l-[#BD984D] bg-[#BD984D]/5"
                        data-testid="customer-checkout"
                      >
                        <div className="text-[10px] uppercase tracking-[0.18em] text-[#A57F39] font-semibold">
                          {t(lang, "checkout_label")}
                        </div>
                        <div className="text-base font-semibold text-[#0C1824] mt-1">
                          {booking.end_date?.slice(0, 10)}
                        </div>
                        <div className="text-sm font-medium text-[#A57F39] mt-0.5">
                          {booking.checkout_time
                            ? (lang === "en"
                                ? `by ${booking.checkout_time}`
                                : `innen kl. ${booking.checkout_time}`)
                            : t(lang, "checkout_time")}
                        </div>
                        {booking.checkout_time && booking.checkout_time > "10:00" && (
                          <div
                            className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-widest text-emerald-700 bg-emerald-100 rounded px-2 py-0.5 mt-1.5"
                            data-testid="customer-checkout-late-badge"
                          >
                            {lang === "en" ? "Late check-out" : "Sen utsjekk"}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="mt-5 pt-5 border-t border-[#E2E8F0]">
                    <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#A57F39]">
                      {t(lang, "guests")}
                    </div>
                    <div className="mt-2 flex items-end gap-3 flex-wrap">
                      <Input
                        type="number"
                        min={1}
                        value={guests}
                        onChange={(e) => setGuests(e.target.value)}
                        className="max-w-[120px] h-11"
                        data-testid="guests-input"
                      />
                      <Button
                        onClick={saveGuests}
                        disabled={savingGuests || parseInt(guests, 10) === booking.num_guests}
                        data-testid="guests-save"
                        className="bg-[#BD984D] hover:bg-[#A57F39] text-white h-11"
                      >
                        {savingGuests ? t(lang, "saving") : t(lang, "update_guests")}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </Card>

            <SelfCheckWizard
              lang={lang}
              bookingId={booking.id}
              kind="checkin"
              enabled={selfCheck.self_checkin_enabled}
              alreadySubmitted={
                selfCheck.submissions.find((s) => s.kind === "checkin") || null
              }
              onSubmitted={() => loadSelfChecks(booking.id)}
            />

            {/* Priority sections – always visible right under the boat card. */}
            {booking && (
              <CustomerAccount lang={lang} key={`acc-${booking.id}-${accountRefreshKey}`} />
            )}

            {booking && (
              <CustomerExtras
                lang={lang}
                booking={booking}
                variant="insurance"
                onOrderPlaced={() => {
                  setAccountRefreshKey((k) => k + 1);
                  // Re-check insurance status so checklists show up as soon
                  // as the Stripe payment webhook has marked the order paid.
                  loadInsuranceStatus(booking.id);
                }}
              />
            )}

            {/* Once insurance is paid, promote the checklists to the main
                portal view so the customer sees exactly what they need to
                do before, during and after the trip – no menu detour. */}
            {insurancePaid && (
              <>
                <CustomerDepartureChecklist lang={lang} bookingId={booking.id} />
                <SelfCheckWizard
                  lang={lang}
                  bookingId={booking.id}
                  kind="checkout"
                  enabled={selfCheck.self_checkout_enabled}
                  alreadySubmitted={
                    selfCheck.submissions.find((s) => s.kind === "checkout") || null
                  }
                  onSubmitted={() => loadSelfChecks(booking.id)}
                />
              </>
            )}

            {/* Menu grid – everything else lives here so the customer never has
                to scroll through 15+ sections to find what they need. */}
            <MenuGrid
              lang={lang}
              booking={booking}
              docs={docs}
              hideIds={insurancePaid ? ["departure", "self-checkout"] : []}
              onSelect={setActiveSection}
            />

            {/* All other sections are rendered inside a full-screen dialog when
                the customer taps their tile in the menu grid above. */}
            <PortalSectionDialog
              lang={lang}
              activeSection={activeSection}
              onClose={() => setActiveSection(null)}
              booking={booking}
              docs={docs}
              docUrl={docUrl}
              accountRefreshKey={accountRefreshKey}
              setAccountRefreshKey={setAccountRefreshKey}
              selfCheck={selfCheck}
              loadSelfChecks={loadSelfChecks}
              onLoadBooking={load}
              report={report}
              setReport={setReport}
              sendReport={sendReport}
              sendingReport={sendingReport}
            />
          </>
        )}

        {/* Privacy / GDPR – always visible to the logged-in customer */}
        <Card className="p-6 sm:p-8">
          <div className="flex items-center gap-2 mb-3">
            <ShieldCheck className="w-5 h-5 text-[#A57F39]" />
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#A57F39]">
              {lang === "en" ? "Privacy" : "Personvern"}
            </div>
          </div>
          <p className="text-sm text-[#2B3F4D]">
            {lang === "en"
              ? "Your rights under GDPR. Export your data or delete your account."
              : "Dine rettigheter etter GDPR. Eksportér dine data eller slett kontoen."}
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <Link to={lang === "en" ? "/privacy" : "/personvern"}>
              <Button variant="outline" className="border-[#0C1824] text-[#0C1824]" data-testid="customer-privacy-link">
                {lang === "en" ? "Privacy policy" : "Personvernerklæring"}
              </Button>
            </Link>
            <Button
              variant="outline"
              onClick={async () => {
                setExporting(true);
                try {
                  const { data } = await api.get("/me/data-export");
                  const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = `canal-boats-my-data-${new Date().toISOString().slice(0, 10)}.json`;
                  document.body.appendChild(a);
                  a.click();
                  a.remove();
                  URL.revokeObjectURL(url);
                  toast.success(lang === "en" ? "Data exported" : "Data eksportert");
                } catch (e) {
                  toast.error(formatApiError(e.response?.data?.detail) || e.message);
                } finally {
                  setExporting(false);
                }
              }}
              disabled={exporting}
              data-testid="customer-export"
              className="border-[#1DA3B3] text-[#1DA3B3] hover:bg-[#1DA3B3]/5"
            >
              <Download className="w-4 h-4 mr-2" />
              {lang === "en" ? "Export my data" : "Last ned mine data"}
            </Button>
            <Button
              variant="outline"
              onClick={() => setDelOpen(true)}
              data-testid="customer-delete-open"
              className="border-red-300 text-red-600 hover:bg-red-50"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              {lang === "en" ? "Delete my account" : "Slett min konto"}
            </Button>
          </div>
        </Card>

        <Dialog open={delOpen} onOpenChange={setDelOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="text-red-600">
                {lang === "en" ? "Permanent deletion" : "Permanent sletting"}
              </DialogTitle>
              <DialogDescription>
                {lang === "en"
                  ? "Type your password and the word DELETE to confirm."
                  : "Skriv passordet ditt og ordet SLETT for å bekrefte."}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>{lang === "en" ? "Password" : "Passord"}</Label>
                <Input
                  type="password"
                  value={delPw}
                  onChange={(e) => setDelPw(e.target.value)}
                  data-testid="customer-delete-password"
                />
              </div>
              <div>
                <Label>{lang === "en" ? "Type DELETE to confirm" : "Skriv SLETT for å bekrefte"}</Label>
                <Input
                  value={delConfirm}
                  onChange={(e) => setDelConfirm(e.target.value)}
                  placeholder={lang === "en" ? "DELETE" : "SLETT"}
                  data-testid="customer-delete-confirm"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDelOpen(false)}>
                {lang === "en" ? "Cancel" : "Avbryt"}
              </Button>
              <Button
                onClick={async () => {
                  // Backend requires Norwegian "SLETT"; accept both languages by mapping
                  const confirmValue = lang === "en" && delConfirm === "DELETE" ? "SLETT" : delConfirm;
                  try {
                    await api.delete("/me/account", { data: { password: delPw, confirm: confirmValue } });
                    toast.success(lang === "en" ? "Account deleted" : "Konto slettet");
                    setUser(false);
                    navigate("/login");
                  } catch (e) {
                    toast.error(formatApiError(e.response?.data?.detail) || e.message);
                  }
                }}
                disabled={
                  !delPw ||
                  !(delConfirm === "SLETT" || (lang === "en" && delConfirm === "DELETE"))
                }
                data-testid="customer-delete-submit"
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                {lang === "en" ? "Delete permanently" : "Slett permanent"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Portal menu grid + section dialog                                   */
/* ------------------------------------------------------------------ */

function tile(icon, en, no, id, opts = {}) {
  return { icon, en, no, id, ...opts };
}

function buildMenuItems({ lang, docs }) {
  const hasDocs = (docs.global?.length || 0) + (docs.for_booking?.length || 0) > 0;
  const items = [
    tile(Receipt,        "Receipt (preview)",  "Kvittering (foreløpig)", "receipt"),
    tile(Clock,          "Change times",       "Endre inn-/utsjekk",     "time-change"),
    tile(Award,          "Captain status",     "Kapteinstatus",          "captain"),
    tile(Camera,         "Boat photos",        "Bilder av båten",        "inspection"),
    tile(ShieldAlert,    "Damage / issues",    "Skade / hendelser",      "incidents"),
    tile(ClipboardList,  "Departure checklist","Avreise-sjekkliste",     "departure"),
    tile(ClipboardCheck, "Self check-out",     "Selv-utsjekk",           "self-checkout"),
    tile(AlertTriangle,  "Report a problem",   "Meld inn en sak",        "report"),
    tile(ShoppingBag,    "Extra services",     "Ekstra tjenester",       "extras"),
    tile(LifeBuoy,       "Life vests",         "Redningsvester",         "lifevests"),
    tile(Camera,         "Share photos",       "Del bilder med oss",     "share-photos"),
    tile(Video,          "Share video",        "Del video med oss",      "share-video"),
    tile(Star,           "Survey",             "Kundeundersøkelse",      "survey"),
    tile(MessageSquare,  "Messages",           "Meldinger",              "messages"),
    tile(MapPin,         "Share location",     "Del posisjon",           "share-location"),
    tile(Bell,           "Notifications",      "Push-varsler",           "push"),
  ];
  if (hasDocs) {
    items.splice(1, 0, tile(FileText, "Documents", "Dokumenter", "documents"));
  }
  return items;
}

// Icons used by the tiles are imported at the top of the file. Receipt is
// pulled from lucide-react along with the others.

function MenuGrid({ lang, booking, docs, onSelect, hideIds = [] }) {
  const items = buildMenuItems({ lang, docs }).filter(
    (it) => !hideIds.includes(it.id),
  );
  const heading = lang === "en"
    ? "Menu – tap what you need"
    : "Meny – trykk på det du trenger";
  const sub = lang === "en"
    ? "Everything else about your trip lives here so you don't have to scroll through the whole page."
    : "Alt annet om turen din ligger her, så du slipper å bla gjennom hele siden.";
  return (
    <Card className="p-4 sm:p-6" data-testid="portal-menu-grid">
      <div className="flex items-center gap-2 mb-1">
        <MenuIcon className="w-5 h-5 text-[#A57F39]" />
        <h2 className="text-lg sm:text-xl font-bold text-[#0C1824]">{heading}</h2>
      </div>
      <p className="text-xs sm:text-sm text-[#2B3F4D] mb-4">{sub}</p>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3">
        {items.map((it) => {
          const Icon = it.icon;
          return (
            <button
              key={it.id}
              type="button"
              onClick={() => onSelect(it.id)}
              data-testid={`portal-menu-${it.id}`}
              className="group flex flex-col items-start gap-2 p-3 sm:p-4 rounded-xl border border-slate-200 bg-white hover:border-[#BD984D]/60 hover:bg-[#BD984D]/5 transition text-left min-h-[92px]"
            >
              <div className="p-2 rounded-lg bg-[#BD984D]/10 text-[#A57F39] group-hover:bg-[#BD984D]/20">
                <Icon className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0 w-full">
                <div className="text-sm font-semibold text-[#0C1824] leading-tight">
                  {lang === "en" ? it.en : it.no}
                </div>
              </div>
              <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-[#A57F39] self-end" />
            </button>
          );
        })}
      </div>
    </Card>
  );
}

function PortalSectionDialog({
  lang, activeSection, onClose, booking, docs, docUrl,
  accountRefreshKey, setAccountRefreshKey,
  selfCheck, loadSelfChecks, onLoadBooking,
  report, setReport, sendReport, sendingReport,
}) {
  const items = buildMenuItems({ lang, docs });
  const meta = items.find((i) => i.id === activeSection);
  const title = meta ? (lang === "en" ? meta.en : meta.no) : "";
  const open = !!activeSection;

  const body = (() => {
    switch (activeSection) {
      case "receipt":
        return <CustomerReceiptPreview lang={lang} booking={booking} />;
      case "documents":
        return <PortalDocumentsSection lang={lang} docs={docs} docUrl={docUrl} />;
      case "time-change":
        return (
          <CustomerTimeChangeRequest
            lang={lang}
            bookingId={booking.id}
            onChanged={onLoadBooking}
          />
        );
      case "captain":
        return (
          <CustomerCaptainCard lang={lang} booking={booking} onSignalled={onLoadBooking} />
        );
      case "inspection":
        return <CustomerBoatInspection lang={lang} booking={booking} />;
      case "incidents":
        return <CustomerIncidentsCard lang={lang} />;
      case "departure":
        return <CustomerDepartureChecklist lang={lang} bookingId={booking.id} />;
      case "self-checkout":
        return (
          <SelfCheckWizard
            lang={lang}
            bookingId={booking.id}
            kind="checkout"
            enabled={selfCheck.self_checkout_enabled}
            alreadySubmitted={
              selfCheck.submissions.find((s) => s.kind === "checkout") || null
            }
            onSubmitted={() => loadSelfChecks(booking.id)}
          />
        );
      case "report":
        return (
          <PortalReportSection
            lang={lang}
            report={report}
            setReport={setReport}
            sendReport={sendReport}
            sendingReport={sendingReport}
            onSent={onClose}
          />
        );
      case "extras":
        return (
          <CustomerExtras
            lang={lang}
            booking={booking}
            variant="extras"
            onOrderPlaced={() => setAccountRefreshKey((k) => k + 1)}
          />
        );
      case "lifevests":
        return <CustomerLifeVests lang={lang} />;
      case "share-photos":
        return <CustomerPhotoShare lang={lang} bookingId={booking.id} />;
      case "share-video":
        return <CustomerVideoShare lang={lang} bookingId={booking.id} />;
      case "survey":
        return <CustomerSurvey lang={lang} />;
      case "messages":
        return <CustomerMessages lang={lang} />;
      case "share-location":
        return <ShareLocationCard lang={lang} />;
      case "push":
        return <PushSettings />;
      default:
        return null;
    }
  })();

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-w-2xl w-full sm:w-auto max-h-[92vh] overflow-y-auto p-0 gap-0"
        data-testid={`portal-section-${activeSection}`}
      >
        <DialogHeader className="sticky top-0 z-10 bg-white border-b border-slate-100 px-4 sm:px-6 py-3 flex-row items-center justify-between">
          <DialogTitle className="text-lg font-bold text-[#0C1824] flex items-center gap-2">
            {meta && <meta.icon className="w-5 h-5 text-[#A57F39]" />}
            {title}
          </DialogTitle>
        </DialogHeader>
        <div className="p-4 sm:p-6">
          {body}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PortalDocumentsSection({ lang, docs, docUrl }) {
  if ((docs.global?.length || 0) + (docs.for_booking?.length || 0) === 0) {
    return (
      <div className="text-sm text-[#2B3F4D]">
        {lang === "en" ? "No documents yet." : "Ingen dokumenter ennå."}
      </div>
    );
  }
  return (
    <div className="space-y-5" data-testid="customer-docs-card">
      {docs.for_booking.length > 0 && (
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#A57F39] mb-2">
            {t(lang, "documents_booking")}
          </div>
          <div className="space-y-2" data-testid="customer-docs-booking">
            {docs.for_booking.map((d) => (
              <a key={d.id} href={docUrl(d.file_id)} target="_blank" rel="noreferrer"
                 data-testid={`customer-doc-${d.id}`}
                 className="flex items-center gap-3 p-3 border border-slate-200 rounded-lg hover:border-[#BD984D]/60 hover:bg-[#BD984D]/5">
                <div className="p-2 bg-[#BD984D]/10 rounded-md text-[#A57F39]">
                  <FileText className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-[#0C1824] truncate">{d.title}</div>
                  {d.description && (
                    <div className="text-xs text-[#2B3F4D] mt-0.5 line-clamp-2">{d.description}</div>
                  )}
                </div>
                <ExternalLink className="w-4 h-4 text-[#A57F39] shrink-0" />
              </a>
            ))}
          </div>
        </div>
      )}
      {docs.global.length > 0 && (
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#A57F39] mb-2">
            {t(lang, "documents_global")}
          </div>
          <div className="space-y-2" data-testid="customer-docs-global">
            {docs.global.map((d) => (
              <a key={d.id} href={docUrl(d.file_id)} target="_blank" rel="noreferrer"
                 data-testid={`customer-doc-${d.id}`}
                 className="flex items-center gap-3 p-3 border border-slate-200 rounded-lg hover:border-[#BD984D]/60 hover:bg-[#BD984D]/5">
                <div className="p-2 bg-[#1DA3B3]/10 rounded-md text-[#1DA3B3]">
                  <FileText className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-[#0C1824] truncate">{d.title}</div>
                  {d.description && (
                    <div className="text-xs text-[#2B3F4D] mt-0.5 line-clamp-2">{d.description}</div>
                  )}
                </div>
                <ExternalLink className="w-4 h-4 text-[#1DA3B3] shrink-0" />
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function PortalReportSection({ lang, report, setReport, sendReport, sendingReport, onSent }) {
  return (
    <div className="space-y-3">
      <p className="text-sm text-[#2B3F4D]">
        {lang === "en"
          ? "Use this form to report damage, faults or forgotten items. We see your report when we log in. For an immediate response, send a WhatsApp to manager Joakim Maylove on +47 900 21 105."
          : "Bruk dette skjemaet for å rapportere skader, feil eller glemte gjenstander. Vi ser meldingen din når vi logger inn. Ønsker du umiddelbart svar, send en WhatsApp til manager Joakim Maylove på +47 900 21 105."}
      </p>
      <div>
        <Label>{t(lang, "report_subject")}</Label>
        <Input
          value={report.title}
          onChange={(e) => setReport({ ...report, title: e.target.value })}
          data-testid="report-title"
        />
      </div>
      <div>
        <Label>{t(lang, "report_description")}</Label>
        <Textarea
          rows={4}
          value={report.description}
          onChange={(e) => setReport({ ...report, description: e.target.value })}
          data-testid="report-description"
        />
      </div>
      <PhotoUploader
        photoIds={report.photo_ids}
        onChange={(ids) => setReport({ ...report, photo_ids: ids })}
        label={t(lang, "add_photo")}
      />
      <Button
        onClick={async () => { await sendReport(); onSent && onSent(); }}
        disabled={sendingReport}
        data-testid="report-submit"
        className="bg-[#BD984D] hover:bg-[#A57F39] text-white h-11 w-full sm:w-auto"
      >
        {sendingReport ? t(lang, "saving") : t(lang, "submit_report")}
      </Button>
    </div>
  );
}

